export const BUG_REPORT_TYPES = ['BUG', 'FEATURE', 'SUPPORT'] as const;

export type BugReportType = (typeof BUG_REPORT_TYPES)[number];

export const BUG_REPORT_STATUSES = [
    'NEW',
    'IN_PROGRESS',
    'REOPENED',
    'PLANNED',
    'FUTURE',
    'DONE',
    'DECLINED',
] as const;

export type BugReportStatus = (typeof BUG_REPORT_STATUSES)[number];

export interface IBugReportPerson {
    readonly id: string | null;
    readonly name: string;
    readonly email: string | null;
}

export interface IBugReportAttachment {
    readonly id: string;
    readonly fileName: string;
    readonly contentType: string;
    readonly sizeBytes: number;
    /**
     * Short-lived presigned URL — do not cache beyond the current view. Null
     * when the object could not be signed; render the file with its download
     * disabled rather than a dead link.
     */
    readonly downloadUrl: string | null;
}

export interface IBugReport {
    readonly id: string;
    readonly type: BugReportType;
    readonly title: string;
    readonly description: string;
    readonly status: BugReportStatus;
    readonly pageUrl: string | null;
    readonly createdAt: string;
    readonly statusChangedAt: string | null;
    readonly organization: { readonly id: string; readonly name: string };
    readonly reportedBy: IBugReportPerson;
    readonly attachments: readonly IBugReportAttachment[];
}

/**
 * Who wrote a reply. `SUPER_ADMIN` is the support team — the reporter never
 * sees which individual admin it was, only that it came from us.
 */
export const BUG_REPORT_COMMENT_AUTHOR_KINDS = ['REPORTER', 'SUPER_ADMIN'] as const;

export type BugReportCommentAuthorKind = (typeof BUG_REPORT_COMMENT_AUTHOR_KINDS)[number];

export interface IBugReportComment {
    readonly id: string;
    readonly body: string;
    readonly authorKind: BugReportCommentAuthorKind;
    readonly author: IBugReportPerson;
    readonly createdAt: string;
    /**
     * Proof sent WITH this reply, as opposed to the report's own
     * `attachments`, which came in with the description. Always empty on a
     * support reply — the file bucket is this application's, so there is
     * nowhere for the portal side to upload to.
     */
    readonly attachments: readonly IBugReportAttachment[];
}

/** A single report plus its triage thread — what `GET /bug-reports/:id` returns. */
export interface IBugReportDetail extends IBugReport {
    readonly adminNote: string | null;
    readonly comments: readonly IBugReportComment[];
}

export interface IBugReportQuery {
    readonly page?: number;
    readonly limit?: number;
}

export interface IBugReportListResult {
    readonly data: readonly IBugReport[];
    readonly page: number;
    readonly limit: number;
    readonly total: number;
    readonly totalPages: number;
}

export interface IGetBugReportUploadUrlInput {
    readonly fileName: string;
    readonly contentType: string;
    readonly sizeBytes: number;
}

/** An upload that has already landed in storage, awaiting a report to claim it. */
export interface IBugReportAttachmentInput {
    readonly storageKey: string;
    readonly fileName: string;
    readonly contentType: string;
}

export interface ICreateBugReport {
    readonly type: BugReportType;
    readonly title: string;
    readonly description: string;
    readonly pageUrl?: string;
    readonly attachments?: readonly IBugReportAttachmentInput[];
}

/** A reply on an existing report. Text is required; proof is not. */
export interface IAddBugReportComment {
    readonly body: string;
    readonly attachments?: readonly IBugReportAttachmentInput[];
}
